/**
 * Aggregated navigation routes schema for all application areas.
 */
import { z } from 'zod'

const baseFields = {
  id: z.number(),
  area: z.enum(['admin', 'dashboard', 'user', 'public', 'marketing']),
  group: z.enum(['entities', 'support', 'user', 'marketing', 'tools', 'social', 'landings', 'creationAssistant', 'studios']).optional().default('entities'),
  type: z.enum(['link', 'dialog', 'view']),
  translationKey: z.string(),
  icon: z.string().optional().default('sparkles'),
  enabled: z.boolean().optional(),
  featureFlag: z.string().optional(),
  allowedRoles: z.array(z.string()).optional(),
  deniedRoles: z.array(z.string()).optional(),
  permissionKeys: z.array(z.string()).optional(),
  requiresAuth: z.boolean().optional(),
}

const adminAreaFields = {
  actions: z
    .object({ create: z.boolean().optional(), edit: z.boolean().optional(), delete: z.boolean().optional(), view: z.boolean().optional() })
    .optional(),
  columns: z.array(z.string()).optional(),
  entityType: z.string().optional(),
  showCreateButton: z.boolean().optional(),
  createIntent: z.string().optional(),
}

const dashboardAreaFields = {
  entityType: z.string().optional(),
  showCreateButton: z.boolean().optional(),
  createIntent: z.string().optional(),
}

const linkItemSchema = z
  .object({
    ...baseFields,
    ...adminAreaFields,
    ...dashboardAreaFields,
    type: z.literal('link'),
    routeKey: z.string().optional(),
    href: z.string().optional(),
    target: z.string().optional(),
  })
  .strict()

const dialogItemSchema = z
  .object({
    ...baseFields,
    ...adminAreaFields,
    ...dashboardAreaFields,
    type: z.literal('dialog'),
    component: z.string().optional(),
  })
  .strict()

const viewItemSchema = z
  .object({
    ...baseFields,
    ...adminAreaFields,
    ...dashboardAreaFields,
    type: z.literal('view'),
    component: z.string(),
  })
  .strict()

export const navigationRouteItemSchema = z.discriminatedUnion('type', [linkItemSchema, dialogItemSchema, viewItemSchema])

export const navigationRoutesSchema = z.array(navigationRouteItemSchema)

export type NavigationRouteItem = z.infer<typeof navigationRouteItemSchema>
export type NavigationRoutes = z.infer<typeof navigationRoutesSchema>
